import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TsButtonModule, TsButtonVariant } from 'tsui';
import { TsBaseDialog, TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { ObservableInput } from 'rxjs';
import { EMPTY, from } from 'rxjs';
import { catchError, defaultIfEmpty, finalize, take } from 'rxjs/operators';

export interface ConfirmationDialogData {
  dialogTitle: string;
  description: string;
  renderHtml: boolean;
  /**
   * @deprecated Prefer using the component's internal error display logic.
   * This remains for backwards compatibility with existing call sites.
   */
  errorMessage?: string;
  styling?: {
    yes: TsButtonVariant;
  };
  buttonsConfig: {
    yes: string;
    no: string;
  };

  /**
   * Optional loading label shown after the user confirms.
   */
  loadingText?: string;

  /**
   * Optional async action to run when the user clicks "Yes".
   * If provided, the dialog will show a loading indicator and only close when it completes.
   */
  confirmAction?: () => ObservableInput<unknown>;
}

@Component({
  selector: 'confirmation-dialog',
  standalone: true,
  imports: [TsButtonModule, TsDialogComponent, MatProgressSpinnerModule],
  templateUrl: './confirmation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialogComponent implements TsBaseDialog<boolean> {
  protected dialogRef = inject<DialogRef<any>>(DialogRef);
  protected dialogData = inject<ConfirmationDialogData>(DIALOG_DATA);
  private readonly cdr = inject(ChangeDetectorRef);

  protected isLoading = false;
  protected actionErrorMessage: string | null = null;

  get title(): string {
    return this.dialogData?.dialogTitle || 'Confirmation';
  }

  get renderHtml(): boolean {
    return this.dialogData?.renderHtml || false;
  }

  get description(): string {
    return this.dialogData?.description || 'Are you sure?';
  }

  get errorMessage(): string | undefined {
    return this.actionErrorMessage || this.dialogData?.errorMessage;
  }

  get hasError(): boolean {
    return !!this.errorMessage;
  }

  get yesLabel(): string {
    return this.dialogData?.buttonsConfig?.yes || 'Yes';
  }

  get yesStylingVariant(): TsButtonVariant {
    return this.dialogData?.styling?.yes || 'danger';
  }

  get noLabel(): string {
    return this.dialogData?.buttonsConfig?.no || 'No';
  }

  get loadingText(): string {
    return this.dialogData?.loadingText || 'Working…';
  }

  /**
   * Dismiss the dialog (e.g. via the header "X").
   */
  dismiss(): void {
    this.dialogRef.close();
  }

  closeDialog(result: boolean): void {
    this.dialogRef.close(result);
  }

  cancel(): void {
    if (this.isLoading) return;
    this.dialogRef.close(false);
  }

  confirm(): void {
    if (this.isLoading) return;

    this.actionErrorMessage = null;
    const confirmAction = this.dialogData?.confirmAction;

    // Default legacy behavior: close with boolean.
    if (!confirmAction) {
      this.dialogRef.close(true);
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    from(confirmAction())
      .pipe(
        take(1),
        defaultIfEmpty(true),
        catchError((err) => {
          this.actionErrorMessage = err?.error?.message || err?.message || 'Something went wrong. Please try again.';
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((result) => {
        this.dialogRef.close(result);
      });
  }
}
